// components/slides/SlidesDashboard.tsx
'use client';
import { SlidesWelcomeButton } from './SlidesWelcome';
import { SlidesTourButton } from './SlidesTour';

// Slides entry surface - a standalone-tool dashboard styled to match the app's
// native dashboards (Studio Projects / Analyst Tables): the shared Studio
// toolbar (InventoryListToolbar: create + search + sort), the Studio project
// card + grid replicated 1:1 (rounded-2xl cards, aspect-[4/3] cover, chips
// row), the shared loading spinner and italic empty state - populated with the
// server-listed review decks (GET /api/slides/decks via loadDeckIndex). Whole-
// surface PDF drag-drop still opens the New-review modal with the file loaded.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Plus, Presentation, Trash2 } from '@/components/ui/lucide-memo';
import { SlidesDashboardToggle } from './SlidesDashboardToggle';
import { canAccessSlidesTraining } from '#lib/access/slidesAccess';
import { useKernelStore } from '@/kernel/kernelStores';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import type { SlideDeckSummaryDTO } from '#types/types';
import { InventoryListToolbar } from '@/components/portal/InventoryListToolbar';
import type { InventoryFilterOption } from '@/components/portal/InventoryFilterControls';
import { BlurRevealImage } from '@/components/BlurRevealImage';
import { useSlidesStore } from './slidesStore';
import { importPdfAsDeck, type ImportStage } from './pdfImport';
import { guidelineLabel } from './slidesUi';

const DECK_STATUS_LABEL: Record<SlideDeckSummaryDTO['status'], string> = { DRAFT: 'Draft', REVIEWING: 'Reviewing', REVIEWED: 'Reviewed' };
// The same formatter the Studio project card and the header credits pill use, so
// one number does not read as "1,240" here and "1.2k" three surfaces away.
import { formatCreditsCompact } from '#lib/formatCredits';
import { buildAssetRenditionUrl } from '#lib/asset-rendition';

const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

const hasFiles = (e: React.DragEvent): boolean =>
  Array.from(e.dataTransfer.types ?? []).includes('Files');

const SORT_OPTIONS = [
  { label: 'Last updated', field: 'UPDATED' },
  { label: 'Name', field: 'NAME' },
];

const deckName = (deck: SlideDeckSummaryDTO): string => deck.name || 'Untitled review';

// The Training table rides on the shared inventory Table and the entity renderers behind it,
// which the deck dashboard never needs; a static import would pull all of it into every
// Slides visit, the same regression Portal guards against for its own tool surfaces.
const SlidesTrainingView = dynamic(
  () => import('./SlidesTrainingView').then((module) => module.SlidesTrainingView),
  { ssr: false, loading: () => <div className="flex flex-1 items-center justify-center p-12 text-[#6E6E6E]"><Loader2 size={32} className="animate-spin" /></div> }
);

// This surface has no filters and no manual refresh: the toolbar still needs the
// props, so they are module constants rather than literals rebuilt every render.
const EMPTY_FILTER_OPTIONS: InventoryFilterOption[] = [];
const EMPTY_VIEW_FILTERS: Record<string, unknown> = {};
const noFilterKeyLocked = (): boolean => false;
const keepFilters = (candidate: Record<string, unknown>): Record<string, unknown> => candidate;
const noop = (): undefined => undefined;

/**
 * One metric chip on a deck card. `whitespace-nowrap` + `shrink-0` are the
 * point: as flex children with neither, the three chips shrank on a narrow
 * viewport and wrapped INSIDE themselves, rendering "14 / pages" and "Round /
 * 1" stacked over two lines. A chip either fits on its line or moves to the
 * next one whole.
 */

// ---------------------------------------------------------------------------
// Deck card - replicates Studio's project card (thumbnail + info + chips row).

const DeckCover = ({ coverUrl, name, priority }: { coverUrl: string; name: string; priority: boolean }) => {
  const renditionUrl = buildAssetRenditionUrl(coverUrl) ?? coverUrl;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const src = failedUrl === renditionUrl ? coverUrl : renditionUrl;

  if (failedUrl === coverUrl) {
    return <span className="absolute inset-x-0 bottom-4 text-center text-xs text-[#737373]">Preview unavailable</span>;
  }

  return (
    <>
      {loadedUrl !== src && <div className="absolute inset-0 animate-pulse bg-[#1E1E1E]" />}
      <div className={clsx('absolute inset-0 bg-white', loadedUrl === src ? 'opacity-100' : 'opacity-0')}>
        <BlurRevealImage
          key={src}
          src={src}
          alt={name}
          className="absolute inset-0 h-full w-full select-none"
          imgClassName="object-cover object-top"
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'low'}
          durationMs={0}
          minBlurMs={0}
          onReady={() => setLoadedUrl(src)}
          onError={() => setFailedUrl(src)}
        />
      </div>
    </>
  );
};

const DeckCard = React.memo(({
  deck,
  priority,
  opening,
  onOpen,
  onDelete,
}: {
  deck: SlideDeckSummaryDTO;
  priority: boolean;
  opening: boolean;
  // The card passes its own deck back, so the grid hands every card the SAME
  // handler instead of a closure per row - which is what kept React.memo here
  // from ever hitting.
  onOpen: (projectId: string) => void;
  onDelete: (deck: SlideDeckSummaryDTO) => void;
}) => {
  const time = deck.updatedAt ? formatDistanceToNow(new Date(deck.updatedAt), { addSuffix: true }) : '';
  const [confirming, setConfirming] = useState(false);

  const handleOpen = useCallback(() => onOpen(deck.projectId), [onOpen, deck.projectId]);
  const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);
  const cancelConfirm = useCallback(() => setConfirming(false), []);
  const startConfirm = useCallback(() => setConfirming(true), []);
  const confirmDelete = useCallback(() => {
    setConfirming(false);
    onDelete(deck);
  }, [onDelete, deck]);

  // ChatGPT's Library tile, the Studio project card's twin (owner, 2026-09-28): the cover is the
  // card, the name and its status under it, one quiet grey line for the facts that were chips.
  // Delete surfaces on hover beside the name; a second click confirms.
  const credits = `${formatCreditsCompact(deck.creditsUsed)} ${Math.round(deck.creditsUsed) === 1 ? 'credit' : 'credits'}`;
  return (
    <div className="group relative cursor-pointer" data-slides-tour="deck" onClick={handleOpen}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-[16px] bg-[#1B1B1B]">
        <div className="absolute inset-0 flex items-center justify-center">
          <Presentation size={28} color="#3A3A3A" />
        </div>
        {deck.coverUrl && (
          <DeckCover key={deck.coverUrl} coverUrl={deck.coverUrl} name={deckName(deck)} priority={priority} />
        )}
        {opening && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1B1B1B]/60 backdrop-blur-[1px]">
            <div className="flex items-center gap-2 rounded-full border border-[#262626] bg-[#1B1B1B] px-3 py-1.5 text-[#ECECEC] shadow-lg" style={{ fontSize: 13 }}>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/60" />
              Opening…
            </div>
          </div>
        )}
      </div>

      <div className="flex items-start gap-1 pl-1 pt-2.5">
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }} title={deckName(deck)}>
            {deckName(deck)}
          </h3>
          <p className="truncate text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>
            {(deck.kind === 'presentation' ? ['Presentation', time, `${deck.pageCount} page${deck.pageCount === 1 ? '' : 's'}`] : [DECK_STATUS_LABEL[deck.status], guidelineLabel(deck.guidelineKey), time, `${deck.pageCount} page${deck.pageCount === 1 ? '' : 's'}`, `Round ${deck.reviewRound}`, credits]).filter(Boolean).join(' · ')}
          </p>
        </div>
        {!opening && (
          <div className="-mr-1 -mt-1 shrink-0" onClick={stopPropagation} onMouseLeave={cancelConfirm}>
            {confirming ? (
              <div className="flex items-center gap-1">
                <button type="button" onClick={confirmDelete} className="h-8 rounded-full bg-[#E8641E] px-3 font-medium text-white transition-colors hover:bg-[#d4571a]" style={{ fontSize: 13 }}>
                  Delete
                </button>
                <button type="button" onClick={cancelConfirm} className="h-8 rounded-full px-3 text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white" style={{ fontSize: 13 }}>
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                title="Delete review"
                aria-label="Delete review"
                onClick={startConfirm}
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#B4B4B4] opacity-0 transition-[opacity,background-color] hover:bg-white/[0.08] hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
DeckCard.displayName = 'DeckCard';

// ---------------------------------------------------------------------------

const SlidesDashboard = () => {
  const deckIndex = useSlidesStore((s) => s.deckIndex);
  const deckIndexLoaded = useSlidesStore((s) => s.deckIndexLoaded);
  const deckIndexLoading = useSlidesStore((s) => s.deckIndexLoading);
  const deckIndexError = useSlidesStore((s) => s.deckIndexError);
  const loadDeckIndex = useSlidesStore((s) => s.loadDeckIndex);
  const loadDeck = useSlidesStore((s) => s.loadDeck);
  const setActiveProject = useSlidesStore((s) => s.setActiveProject);
  const canTrain = useKernelStore((s) => canAccessSlidesTraining(s.userContext));
  const training = useSlidesStore((s) => s.dashboardView === 'training') && canTrain;

  const [openingId, setOpeningId] = useState<string | null>(null);
  /** openDeck is handed to every memoised card, so it must not be rebuilt when
   *  openingId changes - it reads the guard off this mirror instead. */
  const openingIdRef = useRef<string | null>(null);
  /** Dropping a PDF imports it straight away - no setup step. */
  const [importing, setImporting] = useState<{ name: string; done: number; total: number; stage: ImportStage } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('UPDATED');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const store = useSlidesStore.getState();
    if (!store.deckIndexLoaded) void store.loadDeckIndex();
    const retryAfterReconnect = () => {
      const current = useSlidesStore.getState();
      if (current.deckIndexError) void current.loadDeckIndex();
    };
    window.addEventListener('online', retryAfterReconnect);
    return () => window.removeEventListener('online', retryAfterReconnect);
  }, []);

  const retryIndex = useCallback(() => {
    void useSlidesStore.getState().loadDeckIndex();
  }, []);

  const list = useMemo(() => {
    let arr = deckIndex.slice();
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter(
        (d) =>
          deckName(d).toLowerCase().includes(q) ||
          guidelineLabel(d.guidelineKey).toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return arr.sort((a, b) => {
      if (sortField === 'NAME') return deckName(a).localeCompare(deckName(b)) * dir;
      return (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) * dir; // UPDATED
    });
  }, [deckIndex, search, sortField, sortDir]);

  const onSortChange = useCallback((field: string) => {
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('desc');
    }
  }, [sortField]);

  const currentSortLabel = SORT_OPTIONS.find((o) => o.field === sortField)?.label ?? 'Sort';

  const openDeck = useCallback((projectId: string) => {
    if (openingIdRef.current) return;
    const store = useSlidesStore.getState();
    if (store.decks[projectId]) {
      setActiveProject(projectId);
      return;
    }
    openingIdRef.current = projectId;
    setOpeningId(projectId);
    void loadDeck(projectId)
      .then((d) => {
        if (d) setActiveProject(projectId);
      })
      .finally(() => {
        openingIdRef.current = null;
        setOpeningId(null);
      });
  }, [loadDeck, setActiveProject]);

  const handleDeleteDeck = useCallback((deck: SlideDeckSummaryDTO) => {
    void useSlidesStore.getState()
      .deleteDeck(deck.projectId)
      .then((ok) => {
        if (ok) toast.success(`Deleted "${deckName(deck)}"`);
        else toast.error('Could not delete the review - please try again.');
      });
  }, []);

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  /** Import a dropped/picked PDF immediately. importPdfAsDeck creates the deck
   *  server-side and sets it active, so the editor opens by itself. */
  const importFile = useCallback(async (file: File | null | undefined) => {
    if (!file || importing) return;
    if (!isPdfFile(file)) {
      setImportError('Only PDF files are supported.');
      return;
    }
    setImportError(null);
    setImporting({ name: file.name.replace(/\.[^.]+$/, ''), done: 0, total: 0, stage: 'reading' });
    try {
      await importPdfAsDeck(file, {
        name: file.name.replace(/\.[^.]+$/, '').trim() || undefined,
        guidelineKey: 'inline_unit',
        onProgress: (done, total) => setImporting((prev) => (prev ? { ...prev, done, total } : prev)),
        onStage: (stage) => setImporting((prev) => (prev ? { ...prev, stage } : prev)),
      });
      void loadDeckIndex({ refresh: true });
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(null);
    }
  }, [importing, loadDeckIndex]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!hasFiles(e) || importing) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  }, [importing]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (hasFiles(e)) e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (importing) return;
    void importFile(e.dataTransfer.files?.[0]);
  }, [importing, importFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    void importFile(file);
  }, [importFile]);


  return (
    <div
      className="relative flex h-full w-full flex-col bg-black"
      data-slides-tour-project="dashboard"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {training ? <SlidesTrainingView /> : <>
      <div className="shrink-0">
        <div data-slides-tour="import">
        <InventoryListToolbar
          title="Slides"
          viewSwitch={<SlidesDashboardToggle />}
          onCreateClick={openFilePicker}
          createButtonLabel="Import PDF"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search decks..."
          disableManualRefresh
          refreshIsLoading={false}
          onManualRefresh={noop}
          filterOptions={EMPTY_FILTER_OPTIONS}
          viewFilters={EMPTY_VIEW_FILTERS}
          isFilterKeyLocked={noFilterKeyLocked}
          mergeWithLockedFilters={keepFilters}
          setFilters={noop}
          sortOptions={SORT_OPTIONS}
          activeSortField={sortField}
          activeSortDir={sortDir}
          onSortChange={onSortChange}
          currentSortLabel={currentSortLabel}
          rightToolbarContent={<><SlidesWelcomeButton /><SlidesTourButton /></>}
        />
        </div>
      </div>

      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        <div className="grid grid-cols-2 gap-4 gap-y-7 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((deck, index) => (
            <DeckCard
              key={deck.projectId}
              deck={deck}
              priority={index < 8}
              opening={openingId === deck.projectId}
              onOpen={openDeck}
              onDelete={handleDeleteDeck}
            />
          ))}
        </div>

        {!deckIndexLoaded && !deckIndexError && deckIndex.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-4 p-12 text-white/20">
            <Loader2 size={32} className="animate-spin" />
            <span className="text-sm font-medium">Loading...</span>
          </div>
        )}

        {deckIndexError && (
          <div role="alert" className="flex items-center justify-center gap-3 p-6 text-sm text-[#A3A3A3]">
            <span>{deckIndexError}</span>
            <button type="button" onClick={retryIndex} disabled={deckIndexLoading} className="underline underline-offset-2 disabled:opacity-50">
              Retry
            </button>
          </div>
        )}

        {deckIndexLoaded && !deckIndexError && list.length === 0 && (
          <div className="flex flex-col items-center gap-4 p-12 text-center">
            <div className="text-[#8E8E8E]" style={{ fontSize: 14 }}>
              {search.trim() ? 'No decks match your search.' : 'No decks yet.'}
            </div>
            {!search.trim() && (
              <button
                type="button"
                onClick={openFilePicker}
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[#F0F0F0] px-3 text-black transition-colors hover:bg-white"
                style={{ fontSize: 14 }}
              >
                <Plus size={16} strokeWidth={2.1} />
                Import PDF
              </button>
            )}
          </div>
        )}
      </div>
      </>}

      {/* Whole-surface PDF drop highlight */}
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-50 m-3 flex items-center justify-center rounded-[24px] border-2 border-dashed border-[#444444] bg-[#1A1A1A]/90">
          <div className="rounded-full border border-white/[0.06] bg-[#353535] px-4 py-2 text-sm text-[#ECECEC] shadow-xl">
            Drop the proposal PDF
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Import is a loading state, not a setup wizard. */}
      {importing && (() => {
        const { stage, done, total } = importing;
        const percent = Math.round(stage === 'reading' ? 2 : stage === 'pages' ? 5 + 85 * (total ? done / total : 0) : stage === 'uploading' ? 92 : 97);
        const label = stage === 'reading' ? 'Opening the PDF'
          : stage === 'pages' ? `Page ${Math.min(done + 1, total)} of ${total}`
            : stage === 'uploading' ? 'Finishing the upload' : 'Creating the review';
        return (
          <div data-slides-import role="status" aria-live="polite" className="absolute inset-0 z-50 flex items-center justify-center bg-black">
            <div className="flex w-[320px] max-w-[80%] flex-col items-center gap-3">
              <div className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={{ fontSize: 14 }}>{importing.name}</div>
              <div role="progressbar" aria-label="Import progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
                className="h-1 w-full overflow-hidden rounded-full bg-[#262626]">
                <div className="h-full rounded-full bg-[#ECECEC] transition-[width] duration-300 ease-out" style={{ width: `${percent}%` }} />
              </div>
              <div className="tabular-nums text-[#8E8E8E]" style={{ fontSize: 13 }}>{label}</div>
            </div>
          </div>
        );
      })()}

      {importError && !importing && (
        <div className="absolute bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full border border-white/[0.06] bg-[#353535] px-4 py-2 text-sm text-[#F28B82] shadow-xl">
          {importError}
        </div>
      )}
    </div>
  );
};

export default SlidesDashboard;
